import { createTheme } from "@mui/material";

export interface ColorPalette {
  name: string;
  blueLight: string;
  blueMedium: string;
  blueDark: string;
  blueDarker: string;
  blueSubtle: string;
  cyanBright: string;
  greenLight: string;
  greenMedium: string;
  greenDark: string;
  greenPastel: string;
  greenSuccess: string;
  gradient: string;
  purpleLight: string;
  purpleMedium: string;
  purpleMediumDark: string;
  purpleDark: string;
  purpleWhite: string;
  loginHeroGradient: string;
  loginHeroGradientMobile: string;
  loginHeroGradientDesktop: string;
  loginBrandPanel: string;
  headerBackground: string;
  tealAccent: string;
  mangoAccent: string;
  redLight: string;
  redMedium: string;
  redDark: string;
  redPastel: string;
  yellowPastel: string;
  greyLight: string;
  greyMedium: string;
  greyDark: string;
  greyBase: string;
  greyBody: string;
  greyHeading: string;
  black: string;
  white: string;
  neutralBackground: string;
  neutral50: string;
  neutral100: string;
  neutral200: string;
  neutral300: string;
  neutral400: string;
  neutral500: string;
  neutral600: string;
  neutral700: string;
  secondaryBackgroundColor: string;
  primaryColor: string;
  secondaryColor: string;
  primaryTextColor: string;
  secondaryTextColor: string;
  borderColor: string;
  resultsNegativeGreen: string;
  redrawParentGrey: string;
}

interface StyleTheme {
  borderRadius: string;
  boxShadow: string;
  largeFontSize: string;
  mediumFontSize: string;
  largeContentWidth: string;
  extraLargeContentWidth: string;
  windowMinWidthTablet: string;
  windowMinWidthDesktop: string;
}

export interface ThemePalette extends ColorPalette, StyleTheme {}

export const themePalette: ThemePalette = {
  name: "normal",
  blueLight: "#D6F4FF",
  blueMedium: "#63CBF6",
  blueDark: "#00678C",
  blueDarker: "#064682",
  blueSubtle: "#EEF5FA",
  cyanBright: "#00BBFF",
  greenLight: "#CEEAE3",
  greenMedium: "#03CBBA",
  greenDark: "#017E72",
  greenPastel: "#EFF8F5",
  greenSuccess: "#60C66A",
  gradient:
    "linear-gradient(48.22deg, #3BBFAD 0%, #298F95 35%, #1A6881 61%, #216382 73%, #365683 91%, #444D84 100%)",
  purpleLight: "#E4DEFF",
  purpleMedium: "#9273FF",
  purpleMediumDark: "#6649B8",
  purpleDark: "#473380",
  purpleWhite: "#FBF7FF",
  loginBrandPanel: "#084153",
  headerBackground: "#084153",
  loginHeroGradient:
    "linear-gradient(81.5deg, rgba(82, 194, 207, 0) 50%, rgba(82, 194, 207, 0.5) 100%), linear-gradient(90deg, #084153 0%, #084153 100%)",
  loginHeroGradientMobile:
    "linear-gradient(83.95deg, rgba(82, 194, 207, 0) 50%, rgba(82, 194, 207, 0.5) 100%), linear-gradient(90deg, #084153 0%, #084153 100%)",
  loginHeroGradientDesktop:
    "linear-gradient(146.85deg, rgba(82, 194, 207, 0) 49.95%, rgba(82, 194, 207, 0.5) 100.05%), linear-gradient(90deg, #084153 0%, #084153 100%)",
  tealAccent: "#52C2CF",
  mangoAccent: "#FFB545",
  redLight: "#EDB193",
  redMedium: "#F0604B",
  redDark: "#C0250F",
  redPastel: "#FFF2F0",
  yellowPastel: "#F8F1AE",
  greyLight: "#B6B2B2",
  greyMedium: "#5D5A5A",
  greyDark: "#171923",
  greyBase: "#231F20",
  greyBody: "#464A53",
  greyHeading: "#433F3F",
  black: "#000000",
  white: "#FFFFFF",
  neutralBackground: "#EEEEEE",
  neutral50: "#F9F9F9",
  neutral100: "#E7EBF2",
  neutral200: "#DBDEE6",
  neutral300: "#C2C6CC",
  neutral400: "#AAADB3",
  neutral500: "#929499",
  neutral600: "#7A7C80",
  neutral700: "#616366",
  secondaryBackgroundColor: "#0c6182",
  primaryColor: "#3BBEB3",
  secondaryColor: "#19aae2",
  primaryTextColor: "#4f4f4f",
  secondaryTextColor: "#4f4f4f",
  borderColor: "#3BBEB3",
  borderRadius: "5px",
  boxShadow: "0px 6px 11px rgba(0, 0, 0, 0.04)",
  resultsNegativeGreen: "#6ACC74",
  redrawParentGrey: "#E9E7E7",
  largeFontSize: "1.8rem",
  mediumFontSize: "1.3rem",
  largeContentWidth: "853px;",
  extraLargeContentWidth: "1600px",
  windowMinWidthTablet: "600px",
  windowMinWidthDesktop: "1440px",
};

declare module "@mui/material/styles" {
  interface Palette {
    custom: {
      redMedium: string;
      headerGradient: string;
      loginHeroGradientMobile: string;
      neutral50: string;
      neutral100: string;
      neutral200: string;
      neutral300: string;
    };
  }
  interface PaletteOptions {
    custom?: {
      redMedium?: string;
      headerGradient?: string;
      loginHeroGradientMobile?: string;
      neutral50?: string;
      neutral100?: string;
      neutral200?: string;
      neutral300?: string;
    };
  }
}

declare module "@mui/material/Button" {
  interface ButtonPropsVariantOverrides {
    "unity-primary": true;
    "unity-outline": true;
    "unity-ghost": true;
    "unity-outline-orange": true;
  }
}

export const materialTheme = createTheme({
  palette: {
    primary: {
      main: themePalette.greenMedium,
      dark: themePalette.greenDark,
    },
    secondary: {
      dark: themePalette.white,
      main: themePalette.secondaryBackgroundColor,
    },
    background: {
      default: themePalette.white,
    },
    custom: {
      redMedium: themePalette.redMedium,
      headerGradient:
        "linear-gradient(48.22deg, #3BBFAD 0%, #298F95 35%, #1A6881 61%, #216382 73%, #365683 91%, #444D84 100%)",
      loginHeroGradientMobile: themePalette.loginHeroGradientMobile,
      neutral50: themePalette.neutral50,
      neutral100: themePalette.neutral100,
      neutral200: themePalette.neutral200,
      neutral300: themePalette.neutral300,
    },
    contrastThreshold: 3,
    tonalOffset: 0.2,
  },
  typography: {
    fontFamily: "light",
  },
  components: {
    MuiButton: {
      variants: [
        {
          props: { variant: "unity-primary" },
          style: {
            backgroundColor: themePalette.redMedium,
            color: themePalette.white,
            "&:hover": {
              backgroundColor: themePalette.redDark,
            },
          },
        },
        {
          props: { variant: "unity-outline" },
          style: {
            borderColor: themePalette.greenMedium,
            color: themePalette.greenMedium,
            borderWidth: "2px",
            borderStyle: "solid",
          },
        },
        {
          props: { variant: "unity-ghost" },
          style: {
            backgroundColor: "transparent",
            color: themePalette.redMedium,
            "&:hover": {
              backgroundColor: themePalette.redPastel,
            },
          },
        },
        {
          props: { variant: "unity-outline-orange" },
          style: {
            color: themePalette.redMedium,
            borderColor: themePalette.redMedium,
            borderWidth: "2px",
            borderStyle: "solid",
            "&:hover": {
              backgroundColor: themePalette.redPastel,
            },
          },
        },
      ],
      styleOverrides: {
        root: {
          fontWeight: "bold",
          borderRadius: "30px",
          textTransform: "none",
        },
        sizeLarge: {
          padding: "11px 24px",
          fontSize: "20px",
        },
        sizeSmall: {
          padding: "4px 16px",
          fontSize: "14px",
        },
      },
    },
  },
});
